import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../hooks/useSidebar';
import { Search, Plus, Filter, Download, Tag, Link2, AlertTriangle, Sparkles } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import HAZIDWorksheet from './components/HAZIDWorksheet';
import HazardMapping from './components/HazardMapping';
import DeviationIdentification from './components/DeviationIdentification';
import WhatIfChecklist from './components/WhatIfChecklist';
import HazardLibrary from './components/HazardLibrary';
import AIHazardSuggestions from './components/AIHazardSuggestions';
import EquipmentTagging from './components/EquipmentTagging';
import WorkPermitLinking from './components/WorkPermitLinking';
import Icon from '../../components/AppIcon';


const RiskIdentificationDashboard = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState('hazid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [showPermitLinking, setShowPermitLinking] = useState(false);

  const tabs = [
    { id: 'hazid', label: 'HAZID Worksheet', icon: AlertTriangle },
    { id: 'mapping', label: 'Hazard Mapping', icon: Tag },
    { id: 'deviation', label: 'Deviation ID', icon: Filter },
    { id: 'whatif', label: 'What-If Checklist', icon: Search },
    { id: 'library', label: 'Hazard Library', icon: Download },
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'offshore-alpha', label: 'Offshore Platform Alpha' },
    { value: 'offshore-bravo', label: 'Offshore Platform Bravo' },
    { value: 'refinery-01', label: 'Refinery 01' },
    { value: 'terminal-west', label: 'Terminal West' },
  ];

  const riskLevels = [
    { value: 'all', label: 'All Risk Levels' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  const summaryMetrics = {
    totalHazards: 324,
    criticalRisks: 18,
    equipmentTagged: 156,
    linkedPermits: 89,
    pendingReview: 23,
    aiSuggestions: 47,
  };

  return (
    <div className={`flex-1 overflow-y-auto bg-gray-50`}>
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Risk Identification Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">
              Comprehensive HAZID and risk management workspace
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
          >
            Return to Dashboard
          </Button>
        </div>

        {/* Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-900">{summaryMetrics?.totalHazards}</div>
            <div className="text-xs text-blue-600 mt-1">Total Hazards</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-900">{summaryMetrics?.criticalRisks}</div>
            <div className="text-xs text-red-600 mt-1">Critical Risks</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-900">{summaryMetrics?.equipmentTagged}</div>
            <div className="text-xs text-purple-600 mt-1">Equipment Tagged</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-900">{summaryMetrics?.linkedPermits}</div>
            <div className="text-xs text-green-600 mt-1">Linked Permits</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-900">{summaryMetrics?.pendingReview}</div>
            <div className="text-xs text-yellow-600 mt-1">Pending Review</div>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-indigo-900">{summaryMetrics?.aiSuggestions}</div>
            <div className="text-xs text-indigo-600 mt-1">AI Suggestions</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setShowAISuggestions(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Suggestions
          </Button>
          <Button
            onClick={() => setShowPermitLinking(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Link Permits
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            New HAZID Entry
          </Button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search hazards, equipment, or deviations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              icon={<Search className="w-5 h-5 text-gray-400" />}
            />
          </div>
          <div className="flex gap-3">
            <Select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e?.target?.value)}
              options={locations}
              className="w-48"
            />
            <Select
              value={filterRiskLevel}
              onChange={(e) => setFilterRiskLevel(e?.target?.value)}
              options={riskLevels}
              className="w-48"
            />
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-1 overflow-x-auto">
          {tabs?.map((tab) => {
            const Icon = tab?.icon;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap
                  ${activeTab === tab?.id
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab?.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {activeTab === 'hazid' && (
          <HAZIDWorksheet 
            searchQuery={searchQuery}
            filterLocation={filterLocation}
            filterRiskLevel={filterRiskLevel}
            onEquipmentTag={(equipment) => setSelectedEquipment(equipment)}
          />
        )}
        {activeTab === 'mapping' && (
          <HazardMapping 
            searchQuery={searchQuery}
            filterLocation={filterLocation}
          />
        )}
        {activeTab === 'deviation' && (
          <DeviationIdentification 
            searchQuery={searchQuery}
            filterLocation={filterLocation}
          />
        )}
        {activeTab === 'whatif' && (
          <WhatIfChecklist 
            searchQuery={searchQuery}
            filterLocation={filterLocation}
          />
        )}
        {activeTab === 'library' && (
          <HazardLibrary 
            searchQuery={searchQuery}
          />
        )}
      </div>

      {/* AI Suggestions Modal */}
      {showAISuggestions && (
        <AIHazardSuggestions onClose={() => setShowAISuggestions(false)} />
      )}

      {/* Equipment Tagging Modal */}
      {selectedEquipment && (
        <EquipmentTagging 
          equipment={selectedEquipment}
          onClose={() => setSelectedEquipment(null)}
        />
      )}

      {/* Work Permit Linking Modal */}
      {showPermitLinking && (
        <WorkPermitLinking onClose={() => setShowPermitLinking(false)} />
      )}
    </div>
  );
};

export default RiskIdentificationDashboard;